import { useRef } from "react";
import ServerSideSearchBar from "../../../../components/common/table/ServerSideSearchBar";
import { PaginatedData } from "../../../../hooks/PaginationHooks";
import { useAppDispatch } from "../../../../hooks/StoreHooks";
import { setEditMode, setProcessingRequest } from "../../../../store/reducers/tableManagementReducer";
import { useEditMode } from "../../../../store/selectors/tableManagementSelector";
import SearchBarButtonContainer from "../../../../components/common/table/SearchBarButtonContainer";
import IncentivationTable from "./IncentivationTable";
import { SearchBarWrapper } from "../../../../components/common/stylings/SearchBarWrapper.styles";
import { ActionModule, ActionModuleEmployeeResponse } from "../../../../types/actionModule/actionModule";
import { BaseRewardCalculationInformation } from "../../../../types/actionModule/rewardCalculationInformation";
import { PermissionEntityType } from "../../../../types/permissionType";
import { usePermissionChecker } from "../../../../hooks/usePermissionChecker";
import { saveIncentivationsThunk } from "../../../../store/thunk/incentivationThunk";
import { CallResult } from "../../../../api/Requests";
import { DatePicker } from "@mui/x-date-pickers";
import i18n from "../../../../i18n";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import { Box } from "@mui/material";
import { GeneralButton } from "../../../../common/ui/GeneralButton";

interface Props {
    actionModule: ActionModule;
    startDate: Dayjs;
    endDate: Dayjs;
    onDateRangeChange: (startDate: Dayjs, endDate: Dayjs) => void;
    onResetDateRange: () => void;
    paginatedData: PaginatedData<ActionModuleEmployeeResponse>;
}

export default function ActionModuleIncentivation({
    actionModule,
    paginatedData,
    startDate,
    endDate,
    onDateRangeChange,
    onResetDateRange,
}: Props) {
    const dispatch = useAppDispatch();
    const editMode = useEditMode();

    const { userHasPermission } = usePermissionChecker();
    const canIncentivate = userHasPermission(PermissionEntityType.ActionModules, "Incentivate")
        || userHasPermission(PermissionEntityType.ActionModules, "Incentivate", actionModule.id);

    const updatedInstances = useRef<{
        instanceId: number,
        employeeUserId: string,
        rewardCalculationInformation: BaseRewardCalculationInformation,
    }[]>([]);

    async function saveUpdatedRows() {
        dispatch(setEditMode(false))
        dispatch(setProcessingRequest(true));
        const result = await dispatch(
            saveIncentivationsThunk({
                actionModuleId: actionModule.id,
                updates: updatedInstances.current
            })
        );
        if ((result.payload as CallResult<any>)?.success()) {
            paginatedData.updateTableItems();
        }
        dispatch(setProcessingRequest(false));
        updatedInstances.current = [];
    }

    const handleEditClick = () => {
        dispatch(setEditMode(true));
    };

    const handleRowChanged = (original, updated) => {
        paginatedData.rawSetItems((oldState) => oldState.map(
            (r) => r === original ? updated : r
        ))
    }
    const dateFormat = i18n.language === "de" ? "DD.MM.YYYY" : "MM/DD/YYYY";
    const { t } = useTranslation();

    return <Box>
        <SearchBarWrapper>
            <ServerSideSearchBar onSearchHandler={paginatedData.handleOnSearch} />
            <DatePicker
                label={t("incentivationPage.dateRangeStart")}
                value={startDate}
                onChange={(value) => onDateRangeChange(value!, endDate)}
                format={dateFormat}
            />
            <DatePicker
                label={t("incentivationPage.dateRangeEnd")}
                value={endDate}
                onChange={(value) => onDateRangeChange(startDate, value!)}
                format={dateFormat}
            />
            <GeneralButton appVariant="ghost" onClick={onResetDateRange}>
                {t("incentivationPage.clearFilters")}
            </GeneralButton>
            {canIncentivate && (
                <SearchBarButtonContainer
                    onEditClick={editMode ? undefined : handleEditClick}
                    onSaveClick={editMode ? saveUpdatedRows : undefined}
                />
            )}
        </SearchBarWrapper>
        <IncentivationTable
            paginatedData={paginatedData}
            actionModule={actionModule}
            startDate={startDate}
            endDate={endDate}
            onIncentivationUpdated={(employeeId, instanceId, updated) => {
                updatedInstances.current = [
                    ...updatedInstances.current.filter(a => a.instanceId !== instanceId),
                    {
                        instanceId,
                        rewardCalculationInformation: updated,
                        employeeUserId: employeeId
                    }
                ];
            }}
            onEmployeeIncentivationChanged={handleRowChanged} />
    </Box>
}